import { useCallback, useContext } from "react"

import { NotificationContext } from "@context"

import { 
  CREATE_BOOKING_SLOT_MUTATION,
  GET_TIMETABLE, 
  GET_TIMETABLE_BOOKED_SLOT_DETAILS,
  GET_TIMETABLE_CHANGES, 
} from "@graphql"

import { getGraphQLErrorMessage, getInitialsFromParts } from "@helpers"

import { 
  NetworkStatus, 
  useLazyQuery, 
  useMutation, 
  useQuery 
} from "@apollo/client"

const useTimetable = (sessionId) => {
  const { notify } = useContext(NotificationContext)

  const { data, loading, error, refetch, networkStatus } = useQuery(GET_TIMETABLE, {
    variables: { input: { id: sessionId } },
    skip: !sessionId,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  })

  const isInitialLoad = loading && networkStatus === NetworkStatus.loading
  const isRefetching = networkStatus === NetworkStatus.refetch

  const [getTimetableChanges, { loading: partialLoading, error: partialError }] = useLazyQuery(GET_TIMETABLE_CHANGES, {
    fetchPolicy: "network-only",
  })

  const [getBookedSlotDetails, { data: bookedSlotDetailsData, loading: bookedSlotDetailsLoading, error: bookedSlotDetailsError }] = useLazyQuery(GET_TIMETABLE_BOOKED_SLOT_DETAILS, {
    fetchPolicy: "network-only",
  })

  const [createBookingSlot, { loading: isBookingSlot }] = useMutation(CREATE_BOOKING_SLOT_MUTATION, {
    update: (cache, { data }, { variables }) => {
      const booking = data?.createBookingSlot
      const slotId = variables?.input?.slot_id
      const bookingSlot = booking?.bookingSlots?.find(bookingSlot => bookingSlot.slot_id === slotId)

      if (!bookingSlot) return

      const studentInitials = getInitialsFromParts(bookingSlot?.student?.forename, bookingSlot?.student?.surname).toUpperCase();

      cache.modify({
        id: cache.identify({
          __typename: "SessionTimetableSummary",
          id: sessionId,
        }),
        
        fields: {
          total_available: (totalAvailable) => totalAvailable - 1,
          total_booked: (totalBooked) => totalBooked + 1,
        },
      })

      cache.modify({
        id: cache.identify({
          __typename: "SessionTimetableSlot",
          id: slotId,
        }),
        fields: {
          status: () => "BOOKED",
          booking: () => ({
            __typename: "SessionTimetableBooking",
            id: booking.id,
            student_initials: studentInitials,
            booking_slot_id: bookingSlot.id,
            student_id: bookingSlot.student.id,
          }),
        },
      })
    },

    onError: (errors) => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while booking the slot."), open: true })
  })

  const bookSlot = useCallback((eventId, slotId, studentId, userId, lessonId, meetingTypeId) => {
    return createBookingSlot({
      variables: {
        input: {
          event_id: eventId,
          lesson_id: lessonId,
          slot_id: slotId,
          student_id: studentId,
          user_id: userId,
          quantity: 1,
          meeting_type_id: meetingTypeId,
        },
      },
    }).then((response) => response)
  }, [createBookingSlot])

  const fetchBookedSlotDetails = useCallback((eventId, bookingSlotId) => {
    return getBookedSlotDetails({
      variables: {
        input: {
          event_id: eventId,
          booking_slot_id: bookingSlotId,
        },
      },
    }).then((response) => response?.data?.sessionTimetableBookedSlotDetails)
  }, [getBookedSlotDetails])

  const fullRefetch = useCallback(() => {
    return refetch({ input: { id: sessionId } }).then((response) => response?.data?.sessionTimetable)
  }, [refetch, sessionId])

  const partialRefetch = (lastUpdatedAt) => {
    return getTimetableChanges({ 
      variables: {
        input: { 
          id: sessionId, 
          last_updated_at: lastUpdatedAt 
        } 
      }
    }).then((response) => response.data.sessionTimetableChanges)
  }

  const updateTimetableDeletedBookingCache = (cache, bookingId, bookingSlotId, slotId) => {
    cache.modify({
      id: cache.identify({ __typename: "SessionTimetableSummary", id: sessionId }),
      fields: {
        total_available: (totalAvailable) => totalAvailable + 1,
        total_booked: (totalBooked) => totalBooked - 1,
      },
    })

    cache.modify({
      id: cache.identify({ __typename: "SessionTimetableSlot", id: slotId }),
      fields: {
        status: () => "AVAILABLE",
        booking: () => null,
      },
    })

    cache.evict({
      id: cache.identify({ __typename: "SessionTimetableBooking", id: bookingId, booking_slot_id: bookingSlotId }),
    })

    cache.gc()
  }

  /**
   * Skipped slots are those that were attempted to be updated but couldn't be because they have been booked in the meantime.
   */
  const updateTimetableAvailabilityCache = (cache, updatedSlots, skippedSlots) => {
    skippedSlots.forEach(skippedSlot => {
      cache.modify({
        id: cache.identify({ __typename: "SessionTimetableSlot", id: skippedSlot.id }),
        fields: { 
          status: () => "BOOKED",
          booking: () => {
            const [bookingSlot] = skippedSlot.bookingSlots ?? null
            
            if (!bookingSlot) return null

            return {
              __typename: "SessionTimetableBooking",
              id: bookingSlot.booking_id,
              student_initials: bookingSlot.student.forename.charAt(0) + bookingSlot.student.surname.charAt(0),
              booking_slot_id: bookingSlot.id,
              student_id: bookingSlot.student.id,
            }
          }
        },
      })
    })
      
    updatedSlots.forEach(updatedSlot => {
      cache.modify({
        id: cache.identify({ __typename: "SessionTimetableSlot", id: updatedSlot.id }),
        fields: { status: () => updatedSlot.status.toUpperCase() },
      })
    })
  }

  return {
    bookSlot,
    isBookingSlot,
    bookedSlotDetails: bookedSlotDetailsData?.getBookedSlotDetails ?? null,
    bookedSlotDetailsLoading,
    bookedSlotDetailsError,
    fetchBookedSlotDetails,
    timetable: data ? data.sessionTimetable : null,
    loading: isInitialLoad,
    isRefetching,
    error,
    fullRefetch,
    partialError,
    partialLoading,
    partialRefetch,
    updateTimetableAvailabilityCache,
    updateTimetableDeletedBookingCache,
  }
}

export default useTimetable